import React from 'react';
import { track } from '../utils/mixpanelTracking';

/**
 * A wrapper component that adds Mixpanel tracking to any element
 * 
 * Usage:
 * <MixpanelTrackingWrapper 
 *   eventName="Button Clicked" 
 *   properties={{ button_name: 'Save', context: 'Form' }}
 * >
 *   <Button>Save</Button>
 * </MixpanelTrackingWrapper>
 */
const MixpanelTrackingWrapper = ({ 
  children, 
  eventName, 
  properties = {},
  trackOnMount = false,
  trackOnUnmount = false,
  trackOnClick = true,
  trackOnHover = false,
  id
}) => {
  // Track on component mount
  React.useEffect(() => {
    if (trackOnMount) {
      track(eventName || 'Component Mounted', {
        component_id: id,
        ...properties
      });
    }
    
    return () => {
      if (trackOnUnmount) {
        track(eventName || 'Component Unmounted', {
          component_id: id,
          ...properties
        });
      }
    };
  }, [trackOnMount, trackOnUnmount, eventName, id, properties]);
  
  // Handle click event
  const handleClick = React.useCallback((e) => {
    if (trackOnClick) {
      track(eventName || 'Element Clicked', {
        component_id: id,
        ...properties
      });
    }
    
    // Call the original onClick if it exists
    if (React.isValidElement(children) && children.props.onClick) {
      children.props.onClick(e);
    }
  }, [trackOnClick, eventName, id, properties, children]);
  
  // Handle hover event
  const handleMouseEnter = React.useCallback(() => {
    if (trackOnHover) {
      track(eventName || 'Element Hovered', {
        component_id: id,
        ...properties
      });
    }
    
    // Call the original onMouseEnter if it exists
    if (React.isValidElement(children) && children.props.onMouseEnter) {
      children.props.onMouseEnter();
    }
  }, [trackOnHover, eventName, id, properties, children]);
  
  // Clone the child element with our tracking props
  if (!React.isValidElement(children)) {
    return children;
  }
  
  return React.cloneElement(children, {
    'data-mp-id': id || undefined,
    'data-mp-track': eventName || undefined,
    onClick: handleClick,
    onMouseEnter: trackOnHover ? handleMouseEnter : children.props.onMouseEnter,
  });
};

export default MixpanelTrackingWrapper;
